import type { OrderDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import {
  invoicePdfUrl,
  useCancelOrder,
  useDeliverOrder,
  useOrder,
  usePlaceOrder,
  useReturnOrder,
  useSetLines,
  useSetOrderPoints,
  useShipOrder,
  useUpdateOrder,
} from '../api/orders.js';
import { useOrderPoints } from '../api/loyalty.js';
import { Can, useCurrentUser } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime, formatMoney, parseMoneyInput, toMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';
import { ManualPaymentForm, ReasonAction } from './OrderParts.js';
import { orderStatusBadge, paymentBadge } from './OrdersPage.js';
import { Breakdown, breakdownText, ProductPicker } from './PricingParts.js';

const to = t.orders;

interface EditLine {
  sku: string;
  name: string;
  qty: number;
  override: { mode: 'price' | 'percent'; value: number; reason: string } | null;
}

const editLinesOf = (order: OrderDto): EditLine[] =>
  order.lines.map((l) => ({ sku: l.sku, name: l.name, qty: l.qty, override: l.override }));

/** The manager's price override for one line: new price or percent off, always with a reason. */
function OverrideForm({
  line,
  onApply,
  onClose,
}: {
  line: EditLine;
  onApply: (override: EditLine['override']) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'price' | 'percent'>(line.override?.mode ?? 'price');
  const [value, setValue] = useState(
    line.override
      ? line.override.mode === 'price'
        ? toMoneyInput(line.override.value)
        : String(line.override.value).replace('.', ',')
      : '',
  );
  const [reason, setReason] = useState(line.override?.reason ?? '');
  const parsed = mode === 'price' ? parseMoneyInput(value) : Number(value.trim().replace(',', '.'));
  const valid =
    value.trim() !== '' &&
    parsed !== null &&
    Number.isFinite(parsed) &&
    parsed >= 0 &&
    (mode === 'price' || parsed <= 100) &&
    reason.trim() !== '';
  return (
    <div className="inline-form override-form">
      <label>
        {to.overrideMode}
        <select
          aria-label={to.overrideMode}
          value={mode}
          onChange={(e) => setMode(e.target.value as 'price' | 'percent')}
        >
          <option value="price">{to.overrideModes.price}</option>
          <option value="percent">{to.overrideModes.percent}</option>
        </select>
      </label>
      <label>
        {to.overrideValue}
        <input
          aria-label={to.overrideValue}
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      <label className="grow">
        {to.reason}
        <input
          aria-label={`${to.override}: ${to.reason}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="primary"
        disabled={!valid}
        onClick={() => onApply({ mode, value: parsed!, reason })}
      >
        {to.overrideApply}
      </button>
      {line.override && (
        <button type="button" onClick={() => onApply(null)}>
          {to.overrideClear}
        </button>
      )}
      <button type="button" onClick={onClose}>
        {t.common.cancel}
      </button>
    </div>
  );
}

/**
 * Lines with live prices from the server (ORD-05). A draft saves every change at once; a placed
 * order collects changes and saves them together with a reason (ORD-08).
 */
function LinesSection({ order, editable }: { order: OrderDto; editable: boolean }) {
  const me = useCurrentUser();
  const save = useSetLines(order.id);
  const isDraft = order.status === 'draft';
  const [lines, setLines] = useState<EditLine[]>(editLinesOf(order));
  const [dirty, setDirty] = useState(false);
  const [reason, setReason] = useState('');
  const [overriding, setOverriding] = useState<number | null>(null);
  const canOverride = me.permissions.includes('orders.override');

  const change = (next: EditLine[]) => {
    setLines(next);
    if (isDraft) {
      save.mutate({ lines: next.map(({ name: _, ...l }) => l) });
    } else {
      setDirty(true);
    }
  };
  const priced = (sku: string) => order.lines.find((l) => l.sku === sku);

  return (
    <div className="card">
      <h2>{to.linesTitle}</h2>
      {lines.length === 0 ? (
        <p className="muted">{to.noLines}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t.catalogue.sku}</th>
              <th>{t.catalogue.name}</th>
              <th className="num">{to.qty}</th>
              <th className="num">{to.base}</th>
              <th className="num">{to.unit}</th>
              <th className="num">{to.lineTotal}</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const p = dirty ? undefined : priced(line.sku);
              return (
                <tr key={line.sku}>
                  <td>
                    <code>{line.sku}</code>
                  </td>
                  <td>
                    {line.name}
                    {p && <Breakdown breakdown={p.breakdown} />}
                    {line.override && (
                      <div className="muted hint">
                        {to.override}: {line.override.reason}
                      </div>
                    )}
                    {overriding === i && (
                      <OverrideForm
                        line={line}
                        onClose={() => setOverriding(null)}
                        onApply={(override) => {
                          setOverriding(null);
                          change(lines.map((l, j) => (j === i ? { ...l, override } : l)));
                        }}
                      />
                    )}
                  </td>
                  <td className="num">
                    {editable ? (
                      <input
                        type="number"
                        min={1}
                        max={10000}
                        className="qty"
                        aria-label={`${to.qty}: ${line.sku}`}
                        defaultValue={line.qty}
                        onBlur={(e) => {
                          const qty = Math.max(1, Math.floor(Number(e.target.value) || 1));
                          if (qty !== line.qty) {
                            change(lines.map((l, j) => (j === i ? { ...l, qty } : l)));
                          }
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                      />
                    ) : (
                      line.qty
                    )}
                  </td>
                  <td className="num">{p ? formatMoney(p.basePrice) : '—'}</td>
                  <td className="num" title={p ? breakdownText(p.breakdown) : undefined}>
                    {p ? formatMoney(p.unitPrice) : '…'}
                  </td>
                  <td className="num">{p ? formatMoney(p.lineTotal) : '…'}</td>
                  {editable && (
                    <td className="actions">
                      {canOverride && (
                        <button type="button" onClick={() => setOverriding(i)}>
                          {to.override}
                        </button>
                      )}
                      <button
                        type="button"
                        className="icon"
                        aria-label={`${t.common.remove}: ${line.sku}`}
                        onClick={() => change(lines.filter((_, j) => j !== i))}
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {editable && (
        <ProductPicker
          label={to.addProduct}
          onPick={(p) => {
            const existing = lines.findIndex((l) => l.sku === p.sku);
            change(
              existing >= 0
                ? lines.map((l, j) => (j === existing ? { ...l, qty: l.qty + 1 } : l))
                : [...lines, { sku: p.sku, name: p.name, qty: 1, override: null }],
            );
          }}
        />
      )}
      {editable && !isDraft && dirty && (
        <div className="inline-form">
          <span className="muted">{to.unsaved}</span>
          <input
            aria-label={to.editReason}
            placeholder={to.editReason}
            className="grow"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="button"
            className="primary"
            disabled={!reason.trim() || save.isPending}
            onClick={() => save.mutate({ lines: lines.map(({ name: _, ...l }) => l), reason })}
          >
            {to.saveLines}
          </button>
        </div>
      )}
      <ErrorText error={save.error} />
      <dl className="totals">
        <dt>{to.subtotal}</dt>
        <dd>{formatMoney(order.subtotal)}</dd>
        {order.discountTotal > 0 && (
          <>
            <dt>{to.discountTotal}</dt>
            <dd>−{formatMoney(order.discountTotal)}</dd>
          </>
        )}
        {order.pointsValue > 0 && (
          <>
            <dt>{to.points}</dt>
            <dd>−{formatMoney(order.pointsValue)}</dd>
          </>
        )}
        <dt>{to.grandTotal}</dt>
        <dd>
          <strong>{formatMoney(order.total)}</strong>
        </dd>
      </dl>
    </div>
  );
}

/** Q-points on the order (LOY-04): the limit, what it spends, earns or earned. */
function PointsSection({ order, editable }: { order: OrderDto; editable: boolean }) {
  const tl = t.loyalty;
  const points = useOrderPoints(order.id, order.updatedAt);
  const save = useSetOrderPoints(order.id);
  const [value, setValue] = useState(String(order.pointsRedeemed));
  const [reason, setReason] = useState('');
  const isDraft = order.status === 'draft';
  return (
    <QueryState query={points}>
      {(p) => {
        const nothing = p.maxPoints === 0 && p.toEarn === 0 && !p.earned && p.pointsRedeemed === 0;
        if (nothing && order.customerType === 'b2b') return null;
        const n = Number(value);
        const valid =
          value.trim() !== '' &&
          Number.isInteger(n) &&
          n >= 0 &&
          n <= p.maxPoints &&
          n !== p.pointsRedeemed;
        return (
          <div className="card">
            <h2>{tl.orderTitle}</h2>
            {nothing ? (
              <p className="muted">{tl.orderNone}</p>
            ) : (
              <>
                {p.earned && p.earned.status !== 'cancelled' ? (
                  <p>{tl.orderEarned(p.earned.points, tl.statuses[p.earned.status]!)}</p>
                ) : (
                  p.toEarn > 0 && <p>{tl.orderToEarn(p.toEarn)}</p>
                )}
                {editable && (p.maxPoints > 0 || p.pointsRedeemed > 0) && (
                  <div className="inline-form">
                    <label>
                      {tl.orderSpend}
                      <input
                        aria-label={tl.orderSpend}
                        inputMode="numeric"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    </label>
                    <span className="muted">{tl.orderAvailable(p.maxPoints)}</span>
                    {!isDraft && (
                      <input
                        aria-label={to.editReason}
                        placeholder={to.editReason}
                        className="grow"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    )}
                    <button
                      type="button"
                      disabled={!valid || (!isDraft && !reason.trim()) || save.isPending}
                      onClick={() =>
                        save.mutate({ points: n, ...(isDraft ? {} : { reason: reason.trim() }) })
                      }
                    >
                      {tl.orderSave}
                    </button>
                  </div>
                )}
                <ErrorText error={save.error} />
              </>
            )}
          </div>
        );
      }}
    </QueryState>
  );
}

function DeliverySection({ order, editable }: { order: OrderDto; editable: boolean }) {
  const update = useUpdateOrder(order.id);
  const a = order.deliveryAddress;
  const [form, setForm] = useState({
    deliveryMethod: order.deliveryMethod,
    recipientName: a?.recipientName ?? '',
    recipientPhone: a?.recipientPhone ?? '',
    city: a?.city ?? '',
    warehouse: a?.warehouse ?? '',
    comment: order.comment ?? '',
    reason: '',
  });
  const placed = order.status !== 'draft';
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const hasAddress = form.recipientName.trim() && form.recipientPhone.trim();
    update.mutate({
      deliveryMethod: form.deliveryMethod,
      deliveryAddress: hasAddress
        ? {
            recipientName: form.recipientName,
            recipientPhone: form.recipientPhone,
            city: form.city.trim() || null,
            warehouse: form.warehouse.trim() || null,
            npCityRef: a?.npCityRef ?? null,
            npWarehouseRef: a?.npWarehouseRef ?? null,
          }
        : null,
      comment: form.comment.trim() || null,
      reason: placed ? form.reason : undefined,
    });
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2>{to.deliveryTitle}</h2>
      <label>
        {to.deliveryMethod}
        <select
          aria-label={to.deliveryMethod}
          value={form.deliveryMethod}
          disabled={!editable}
          onChange={(e) =>
            setForm({ ...form, deliveryMethod: e.target.value as typeof form.deliveryMethod })
          }
        >
          <option value="nova_post">{to.delivery.nova_post}</option>
          <option value="pickup">{to.delivery.pickup}</option>
        </select>
      </label>
      {(['recipientName', 'recipientPhone', 'city', 'warehouse'] as const).map((key) => (
        <label key={key}>
          {to[key]}
          <input aria-label={to[key]} value={form[key]} disabled={!editable} onChange={set(key)} />
        </label>
      ))}
      <label className="span-2">
        {to.comment}
        <input
          aria-label={to.comment}
          value={form.comment}
          disabled={!editable}
          onChange={set('comment')}
        />
      </label>
      {editable && <p className="muted hint">{to.npHint}</p>}
      {editable && placed && (
        <label className="span-2">
          {to.editReason}
          <input
            aria-label={`${to.deliveryTitle}: ${to.reason}`}
            value={form.reason}
            onChange={set('reason')}
          />
        </label>
      )}
      {editable && (
        <div className="actions">
          <button type="submit" disabled={update.isPending || (placed && !form.reason.trim())}>
            {to.saveDetails}
          </button>
          {update.isSuccess && <span className="ok-text">{t.common.saved}</span>}
        </div>
      )}
      <ErrorText error={update.error} />
    </form>
  );
}

function PlaceSection({ order }: { order: OrderDto }) {
  const place = usePlaceOrder(order.id);
  const [method, setMethod] = useState(order.paymentMethod ?? 'cod');
  const [deferred, setDeferred] = useState(false);
  const canDefer = order.customerType === 'b2b' && method === 'bank_transfer';
  return (
    <div className="card">
      <h2>{to.placeTitle}</h2>
      <p className="muted hint">{to.placeHint}</p>
      <div className="inline-form">
        <label>
          {to.paymentMethod}
          <select
            aria-label={to.paymentMethod}
            value={method}
            onChange={(e) => setMethod(e.target.value as typeof method)}
          >
            {Object.entries(to.methods).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {canDefer && (
          <label className="check">
            <input
              type="checkbox"
              checked={deferred}
              onChange={(e) => setDeferred(e.target.checked)}
            />
            {to.deferred}
          </label>
        )}
        <button
          type="button"
          className="primary"
          disabled={place.isPending || order.lines.length === 0}
          onClick={() => place.mutate({ paymentMethod: method, deferred: canDefer && deferred })}
        >
          {to.place}
        </button>
      </div>
      <ErrorText error={place.error} />
    </div>
  );
}

function ActionsSection({ order }: { order: OrderDto }) {
  const cancel = useCancelOrder(order.id);
  const ship = useShipOrder(order.id);
  const deliver = useDeliverOrder(order.id);
  const giveBack = useReturnOrder(order.id);
  const [ttn, setTtn] = useState('');
  const has = (action: OrderDto['actions'][number]) => order.actions.includes(action);
  if (!['cancel', 'ship', 'deliver', 'return'].some((a) => has(a as never))) return null;
  return (
    <div className="card">
      <h2>{to.actionsTitle}</h2>
      <div className="actions">
        {has('ship') && (
          <Can permission="shipments.manage">
            <input
              aria-label={to.ttn}
              placeholder={to.ttn}
              value={ttn}
              onChange={(e) => setTtn(e.target.value)}
            />
            <button
              type="button"
              className="primary"
              disabled={ship.isPending}
              onClick={() => ship.mutate(ttn)}
            >
              {to.ship}
            </button>
          </Can>
        )}
        {has('deliver') && (
          <Can permission="shipments.manage">
            <button
              type="button"
              className="primary"
              disabled={deliver.isPending}
              onClick={() => deliver.mutate()}
            >
              {to.deliver}
            </button>
          </Can>
        )}
      </div>
      <Can permission="orders.edit">
        {has('return') && (
          <ReasonAction
            label={to.returnOrder}
            hint={to.returnHint}
            pending={giveBack.isPending}
            error={giveBack.error}
            onConfirm={(reason, done) => giveBack.mutate(reason, { onSuccess: done })}
          />
        )}
        {has('cancel') && (
          <ReasonAction
            label={to.cancel}
            danger
            pending={cancel.isPending}
            error={cancel.error}
            onConfirm={(reason, done) => cancel.mutate(reason, { onSuccess: done })}
          />
        )}
      </Can>
      <ErrorText error={ship.error ?? deliver.error} />
    </div>
  );
}

function MoneySection({ order }: { order: OrderDto }) {
  return (
    <div className="card">
      {order.invoices.length > 0 && (
        <>
          <h2>{to.invoicesTitle}</h2>
          <table className="table">
            <tbody>
              {order.invoices.map((i) => (
                <tr key={i.id}>
                  <td>
                    {to.invoiceKinds[i.kind]} № {i.number}
                  </td>
                  <td className="num">{formatMoney(i.amount)}</td>
                  <td>
                    {to.outstanding}: {formatMoney(i.outstanding)}
                  </td>
                  <td>
                    {i.kind !== 'credit_note' &&
                      i.status === 'open' &&
                      `${to.due} ${i.dueDate.split('-').reverse().join('.')}`}
                  </td>
                  <td>
                    <span
                      className={`badge ${i.status === 'paid' ? 'ok' : i.status === 'cancelled' ? 'bad' : ''}`}
                    >
                      {i.kind === 'credit_note' && i.status === 'paid'
                        ? to.creditApplied
                        : to.invoiceStatuses[i.status]}
                    </span>
                  </td>
                  <td>
                    <a href={invoicePdfUrl(order.id, i.id)} target="_blank" rel="noreferrer">
                      {to.pdf}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <h2>{to.paymentsTitle}</h2>
      {order.payments.length === 0 ? (
        <p className="muted">{to.noPayments}</p>
      ) : (
        <table className="table">
          <tbody>
            {order.payments.map((p) => (
              <tr key={p.id}>
                <td>{formatDateTime(p.receivedAt)}</td>
                <td>{t.payments.directions[p.direction]}</td>
                <td className="num">
                  {p.direction === 'refund' ? '−' : ''}
                  {formatMoney(p.amount)}
                </td>
                <td>{to.methods[p.method]}</td>
                <td className="muted">{[p.reference, p.reason].filter(Boolean).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {order.status !== 'draft' && (
        <ManualPaymentForm
          orderId={order.id}
          defaultMethod={order.paymentMethod ?? 'bank_transfer'}
        />
      )}
    </div>
  );
}

function HistorySection({ order }: { order: OrderDto }) {
  return (
    <div className="card">
      <h2>{to.historyTitle}</h2>
      <ul className="history">
        {order.history.map((h, i) => (
          <li key={i}>
            <span className="muted">{formatDateTime(h.at)}</span> ·{' '}
            {to.history[h.action] ?? h.action} · {h.userName ?? to.system}
            {h.reason && <span className="muted"> — {h.reason}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Order editor and card (ORD-13): everything about one order on one page. */
export function OrderPage() {
  const { id = '' } = useParams();
  const order = useOrder(id);
  const me = useCurrentUser();
  return (
    <section className="wide">
      <Link to="/orders">{to.back}</Link>
      <QueryState query={order}>
        {(o) => {
          const editable = o.actions.includes('edit') && me.permissions.includes('orders.edit');
          return (
            // Keyed by updatedAt so every form starts from the saved order after a change.
            <div key={o.updatedAt}>
              <h1>
                {t.orders.title} № {o.number} {orderStatusBadge(o.status)}{' '}
                {o.status !== 'draft' && paymentBadge(o.paymentStatus)}
              </h1>
              <p className="muted">
                {to.customer}: <Link to={`/customers/${o.customerId}`}>{o.customerName}</Link> ·{' '}
                {t.customers.types[o.customerType]} · {to.channels[o.channel]}
                {o.wooOrderId !== null && ` (${to.wooOrder} №${o.wooOrderId})`}
                {o.paymentMethod && ` · ${to.methods[o.paymentMethod]}`}
                {o.deferred && ` · ${to.deferred}`}
                {o.returnUntil
                  ? ` · ${to.returnUntil} ${o.returnUntil.split('-').reverse().join('.')}`
                  : o.returnWindowDays !== null && ` · ${to.returnWindow(o.returnWindowDays)}`}
              </p>
              {o.attention.length > 0 && (
                <div className="card attention">
                  <h2>{to.attentionTitle}</h2>
                  <p className="muted hint">{to.attentionHint}</p>
                  <ul>
                    {o.attention.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              <LinesSection order={o} editable={editable} />
              <PointsSection order={o} editable={editable} />
              <DeliverySection order={o} editable={editable} />
              {o.status === 'draft' && editable && <PlaceSection order={o} />}
              <ActionsSection order={o} />
              {o.return && (
                <div className="card">
                  <h2>{to.returnTitle}</h2>
                  <p>
                    {o.return.reason} · {formatMoney(o.return.refundAmount)}
                  </p>
                </div>
              )}
              <MoneySection order={o} />
              <HistorySection order={o} />
            </div>
          );
        }}
      </QueryState>
    </section>
  );
}
